import { CoordinateSystemInfoProvider, T_ICoordinateSystemInfoProvider } from '@cgs/common';
import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
} from '@cgs/machines';
import {
  Compatibility,
  Container,
  debounce,
  Rect,
  SceneObject,
  Vector2,
  viewportChangeListener,
} from '@cgs/wge';

export class SantaOrientationHandlerComponent {
  private readonly horizontalNode: SceneObject;
  private readonly verticalNode: SceneObject;
  private readonly coordinateSystem: Rect;
  protected readonly root: SceneObject;
  private readonly canvas: HTMLCanvasElement;

  private readonly horizontalProportion: number;
  private readonly verticalProportion: number;

  protected readonly gameStateMachine: GameStateMachine<SpinResponse>;

  private modeOriginalPosition: Vector2 | null = null;

  constructor(container: Container) {
    const resourceComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.root = resourceComponent.root;

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.horizontalNode = this.root.findById<SceneObject>('sizeNodeHorizontal')!;
    this.verticalNode = this.root.findById<SceneObject>('sizeNodeVertical')!;

    const coordinateSystemInfoProvider = container.resolve<CoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );

    this.coordinateSystem = coordinateSystemInfoProvider.coordinateSystem;
    this.canvas = coordinateSystemInfoProvider.canvas;

    this.horizontalProportion = this.horizontalNode.size.x / this.horizontalNode.size.y;
    this.verticalProportion = this.verticalNode.size.y / this.verticalNode.size.x;

    const debouncedResize = debounce(() => this.resize(), 100);

    viewportChangeListener.on(debouncedResize);

    this.gameStateMachine.init.entered.listen(() => this.resize());
    this.gameStateMachine.beginFreeSpinsPopup.leaved.listen(() => this.resize());
    this.gameStateMachine.endFreeSpinsPopup.leaved.listen(() => this.resize());

    this.resize();
  }

  protected resize(): void {
    const modesParent = this.root.findById('Modes')!.parent!;

    const modes = this.root.findById('Modes')!;

    if (!modes) {
      return;
    }

    if (!this.modeOriginalPosition) {
      this.modeOriginalPosition = modes.position.clone();
    }

    modes.position = this.modeOriginalPosition.clone();

    if (!this.horizontalNode || !this.verticalNode) {
      return;
    }

    const isFreeSpins =
      this.gameStateMachine.curResponse &&
      this.gameStateMachine.curResponse.isFreeSpins &&
      this.gameStateMachine.curResponse.freeSpinsInfo!.event !==
        FreeSpinsInfoEvent.FreeSpinsFinished;

    if (!Compatibility.IsMobileBrowser) {
      const windowRatio = window.innerWidth / window.innerHeight;

      this.switchHudMode('landscape');
      modes.stateMachine!.switchToState('horizontal');

      let scale = 1;

      if (windowRatio > 1.9) {
        scale = 0.82;
      } else if (windowRatio > 1.5) {
        scale = 1;
      } else if (windowRatio > 1) {
        scale = 1.5 - windowRatio + 1;
      } else {
        scale = 1.5;
      }

      modesParent.scale = new Vector2(scale, scale);

      return;
    }

    if (!Compatibility.isPortrait) {
      const windowRatio = window.innerHeight / window.innerWidth;
      const canvasRatio = this.canvas.height / this.canvas.width;

      const ratio = Math.max(windowRatio, canvasRatio);

      const commonProportion = this.coordinateSystem.size.x / this.horizontalNode.size.x;

      const proportionCoefficient = 1.5;

      const proportion =
        Math.max(this.verticalProportion, this.horizontalProportion) * proportionCoefficient;

      const scale = Math.min(ratio * proportion, commonProportion);
      modesParent.scale = new Vector2(scale, scale);

      this.switchHudMode('landscape');
      modes.stateMachine!.switchToState('horizontal');
    }

    if (Compatibility.isPortrait) {
      const windowRatio = window.innerWidth / window.innerHeight;
      const canvasRatio = this.canvas.width / this.canvas.height;

      const ratio = Math.max(windowRatio, canvasRatio);

      const commonProportion = this.coordinateSystem.size.y / this.verticalNode.size.y;

      const proportionCoefficient = isFreeSpins ? 1 : 1.2;

      const proportion =
        Math.max(this.verticalProportion, this.horizontalProportion) * proportionCoefficient;

      const scale = Math.max(ratio * proportion, commonProportion);
      modesParent.scale = new Vector2(scale, scale);

      modes.position = this.modeOriginalPosition.clone().subtract(new Vector2(0, 75));

      this.switchHudMode('portrait');
      modes.stateMachine!.switchToState('vertical');
    }
  }

  public update(): void {
    this.resize();
  }

  private switchHudMode(state: string): void {
    this.root.findAllById('hud_mode').forEach((hudMode) => {
      hudMode.stateMachine!.switchToState(state);
    });
  }
}
